import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineS } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import {
  laptopAndAbove,
  mobileAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { useDismissFirstTime } from 'components/forYou/FirstTime/useDismissFirstTime';

import collectBaseballUrl from './assets/collect_baseball.png';
import collectFootballUrl from './assets/collect_football.png';
import collectNbaUrl from './assets/collect_nba.png';
import tradeBaseballUrl from './assets/trade_baseball.png';
import tradeFootballUrl from './assets/trade_football.png';
import tradeNbaUrl from './assets/trade_nba.png';
import winBaseballUrl from './assets/win_baseball.png';
import winFootballUrl from './assets/win_football.png';
import winNbaUrl from './assets/win_nba.png';

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  justify-content: flex-start;
  padding-top: var(--double-unit);
`;

const Row = styled(Vertical).attrs({ gap: 2, center: true })`
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;
  @media ${mobileAndAbove} {
    flex-direction: row;
  }
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: var(--half-unit);
  align-items: center;
  padding-bottom: var(--half-unit);
`;

const Title = styled(HeadlineS)`
  text-transform: uppercase;
`;

const Dismiss = styled(Button)`
  flex-grow: 0;
  justify-self: flex-end;
`;

const FirstTimeBlock = styled.div`
  background-size: cover;
  background-position: center bottom;

  width: auto;
  flex: 1;
  border-radius: var(--unit);
  font-family: var(--rigid-square);
  text-align: center;
  font-size: 40px;
  font-style: normal;
  font-weight: 700;
  line-height: calc(1cap + var(--unit));
  letter-spacing: -1.12px;
  text-transform: uppercase;

  padding: var(--double-unit);

  text-shadow: 0 var(--half-unit) var(--unit) rgba(0, 0, 0, 0.25);
  flex-basis: 200px;
  @media ${mobileAndAbove} {
    font-size: 30px;
    height: 200px;
    flex-basis: var(--flexBasis, fit-content);
  }
  @media ${tabletAndAbove} {
    font-size: 40px;
    padding: var(--triple-unit);
    height: 300px;
  }

  @media ${laptopAndAbove} {
    font-size: 56px;
    padding: var(--quadruple-unit);
    height: 400px;
  }

  &:nth-child(1) {
    background-color: #f2be18;
    background-position: center 30%;
    @media ${tabletAndAbove} {
      background-position: center 30%;
    }
  }
  &:nth-child(2) {
    background-color: #033415;
    background-position: center 40%;
    @media ${tabletAndAbove} {
      background-position: center 60%;
    }
  }
  &:nth-child(3) {
    background-color: #470f74;
    background-position: center 40%;
    @media ${tabletAndAbove} {
      background-position: center 50%;
    }
  }
`;
const assetsBySport = {
  [Sport.FOOTBALL]: [collectFootballUrl, tradeFootballUrl, winFootballUrl],
  [Sport.BASEBALL]: [collectBaseballUrl, tradeBaseballUrl, winBaseballUrl],
  [Sport.NBA]: [collectNbaUrl, tradeNbaUrl, winNbaUrl],
};

export const FirstTime = () => {
  const { sport } = useSportContext();
  const { dismiss, hasDismissed } = useDismissFirstTime();
  const {
    flags: { chooseYourLevelOnboardingActiveSports = [] },
  } = useFeatureFlags();

  const { currentUser } = useCurrentUserContext();

  if (
    hasDismissed ||
    chooseYourLevelOnboardingActiveSports.includes('football')
  ) {
    return null;
  }

  const [collectUrl, tradeUrl, winUrl] = assetsBySport[sport];
  return (
    <Wrapper>
      <Header>
        <div />
        <Title as="h3">
          <FormattedMessage
            id="ForYou.FirstTime.title"
            defaultMessage="First time in the market?"
          />
        </Title>
        {currentUser && (
          <Dismiss
            size="small"
            startIcon={<FontAwesomeIcon icon={faXmark} />}
            color="secondary"
            onClick={dismiss}
          >
            <BodyM as="span">
              <FormattedMessage
                id="ForYou.FirstTime.button"
                defaultMessage="Dismiss"
              />
            </BodyM>
          </Dismiss>
        )}
      </Header>
      <Row>
        <FirstTimeBlock
          style={
            {
              backgroundImage: `url(${collectUrl})`,
              '--flexBasis': '310px',
            } as React.CSSProperties
          }
        >
          <FormattedMessage
            id="ForYou.FirstTime.block.title.1"
            defaultMessage="Collect"
          />
        </FirstTimeBlock>
        <FirstTimeBlock
          style={
            {
              backgroundImage: `url(${tradeUrl})`,
              '--flexBasis': '444px',
            } as React.CSSProperties
          }
        >
          <FormattedMessage
            id="ForYou.FirstTime.block.title.2"
            defaultMessage="Trade & Sell"
          />
        </FirstTimeBlock>
        <FirstTimeBlock
          style={
            {
              backgroundImage: `url(${winUrl})`,
              '--flexBasis': '530px',
            } as React.CSSProperties
          }
        >
          <FormattedMessage
            id="ForYou.FirstTime.text"
            defaultMessage="Play & Win"
          />
        </FirstTimeBlock>
      </Row>
    </Wrapper>
  );
};
